import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CreditCard, Settings, Server, ExternalLink, Loader2, Plus, RefreshCcw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ServerCard } from '@/components/servers/ServerCard';
import { AddServerDialog } from '@/components/servers/AddServerDialog';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import type { Server as ServerType } from '@/types/server';
import {
  fetchServers,
  updateServerDisplayName,
  toggleSecurityUpdates,
  rebootServer,
  forceRebootServer,
  destroyServer,
  addSSHKey,
} from '@/api/servers';

function getProjectIdFromUrl(): string {
  const match = window.location.pathname.match(/\/project\/([^/]+)/);
  if (match) return match[1];
  const params = new URLSearchParams(window.location.search);
  const projectId = params.get('project_id');
  if (projectId) return projectId;
  return 'demo';
}

export function ServersPage() {
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const projectId = routeProjectId || getProjectIdFromUrl();
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['servers', projectId],
    queryFn: () => fetchServers(projectId),
    retry: 1,
  });

  const fallbackProject = {
    id: projectId,
    hashid: projectId,
    name: '',
    has_active_subscription: false,
    is_subscription_ending: false,
    is_subscription_cancelled: false,
    subscription_end_date: null,
    billing_plan: '',
  };

  const servers = data?.servers ?? [];
  const project = data?.project ?? fallbackProject;
  const serverData = data?.server_data;
  const sshPublicKey = data?.ssh_public_key ?? '';

  const updateDisplayNameMutation = useMutation({
    mutationFn: ({ serverId, name }: { serverId: number; name: string }) =>
      updateServerDisplayName(projectId, serverId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', projectId] });
    },
  });

  const toggleSecurityMutation = useMutation({
    mutationFn: ({ serverId, enabled }: { serverId: number; enabled: boolean }) =>
      toggleSecurityUpdates(projectId, serverId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', projectId] });
    },
  });

  const rebootMutation = useMutation({
    mutationFn: (serverId: number) => rebootServer(projectId, serverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', projectId] });
    },
  });

  const forceRebootMutation = useMutation({
    mutationFn: (serverId: number) => forceRebootServer(projectId, serverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', projectId] });
    },
  });

  const destroyMutation = useMutation({
    mutationFn: (serverId: number) => destroyServer(projectId, serverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', projectId] });
    },
  });

  const addSSHKeyMutation = useMutation({
    mutationFn: ({ serverId, key }: { serverId: number; key: string }) =>
      addSSHKey(projectId, serverId, key),
  });

  const handleUpdateDisplayName = async (serverId: number, name: string) => {
    await updateDisplayNameMutation.mutateAsync({ serverId, name });
  };

  const handleToggleSecurityUpdates = async (serverId: number, enabled: boolean) => {
    await toggleSecurityMutation.mutateAsync({ serverId, enabled });
  };

  const handleReboot = async (serverId: number) => {
    await rebootMutation.mutateAsync(serverId);
  };

  const handleForceReboot = async (serverId: number) => {
    await forceRebootMutation.mutateAsync(serverId);
  };

  const handleDestroy = async (serverId: number) => {
    await destroyMutation.mutateAsync(serverId);
  };

  const handleAddSSHKey = async (serverId: number, key: string) => {
    await addSSHKeyMutation.mutateAsync({ serverId, key });
  };

  if (isLoading && !data) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Loading servers...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto w-full px-4 md:px-6">
        {error && (
          <Alert className="mb-6" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unable to load servers</AlertTitle>
            <AlertDescription className="flex items-center gap-2">
              There was a problem loading servers. Please refresh or try again.
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {!project.has_active_subscription && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-5 w-5" />
            <AlertTitle>Active Subscription Required</AlertTitle>
            <AlertDescription>
              <p className="mb-3">
                This project needs an active subscription to create and manage servers.
                Set up billing to get started with Pro or Starter plans.
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="destructive" 
                  onClick={() => window.location.href = `/project/${project.hashid}/checkout`}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Setup Billing
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => window.location.href = `/project/${project.hashid}/settings#billing`}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Billing Settings
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {project.is_subscription_ending && (
          <Alert className="mb-6 border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <AlertTitle className="text-yellow-800 dark:text-yellow-200">Subscription Ending</AlertTitle>
            <AlertDescription className="text-yellow-700 dark:text-yellow-300">
              <p className="mb-3">
                Your subscription will end on <strong>{project.subscription_end_date}</strong>.
                You can continue using all features until then.
              </p>
              <Button 
                variant="outline" 
                className="border-yellow-600 text-yellow-700 hover:bg-yellow-100"
                onClick={() => window.location.href = `/project/${project.hashid}/settings#billing`}
              >
                <RefreshCcw className="h-4 w-4 mr-2" />
                {project.is_subscription_cancelled ? 'Resubscribe' : 'Subscribe'}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Servers</h1>
          {project.has_active_subscription && (
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Server
            </Button>
          )}
        </div>

        {/* Add Server Dialog */}
        {serverData && (
          <AddServerDialog
            open={addDialogOpen}
            onOpenChange={setAddDialogOpen}
            serverData={serverData}
            sshPublicKey={sshPublicKey}
            billingPlan={project.billing_plan}
            projectId={projectId}
          />
        )}

        {servers.length > 0 ? (
          <div className="flex flex-wrap gap-4">
            {servers.map((server: ServerType) => (
              <div key={server.id} className="w-[600px]">
                <ServerCard
                  server={server}
                  projectId={project.hashid}
                  onUpdateDisplayName={handleUpdateDisplayName}
                  onToggleSecurityUpdates={handleToggleSecurityUpdates}
                  onReboot={handleReboot}
                  onForceReboot={handleForceReboot}
                  onDestroy={handleDestroy}
                  onAddSSHKey={handleAddSSHKey}
                />
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-16 text-center">
              <Server className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold text-muted-foreground mb-3">No servers yet</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {error
                  ? 'Unable to load servers right now. Try refreshing.'
                  : 'Get started by adding your first server. You can choose from our managed doploy Cloud servers or connect your own server via SSH.'}
              </p>

              {project.has_active_subscription ? (
                <Button 
                  size="lg" 
                  disabled={!!error}
                  onClick={() => setAddDialogOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Server
                </Button>
              ) : (
                <Button 
                  size="lg" 
                  disabled={!!error}
                  onClick={() => window.location.href = `/project/${project.hashid || projectId}/checkout`}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Setup Billing to Add Servers
                </Button>
              )}

              <Alert className="mt-8 max-w-lg mx-auto text-left">
                <AlertTitle className="flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Need help getting started?
                </AlertTitle>
                <AlertDescription>
                  <p className="mb-2">
                    Check out our comprehensive documentation for step-by-step guides on setting up your first server.
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <a href="https://docs.doploy.io" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3 w-3 mr-1" />
                      View Documentation
                    </a>
                  </Button>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
